import {makeObservable, observable, action, runInAction} from "mobx";
import CommonStore from "../../../../stores/CommonStore";
import {AgencyDocumentSettingsService} from "../../../../service/AgencyDocumentSettingsService";
import AgencyDocumentSettings from "../../../../model/agency/AgencyDocumentSettings";
import DocumentIssuerMode from "../../../../entities/DocumentIssuerMode";
import {showSuccessToast} from "../../../../hooks/Toastify";

class DocumentSettingsTabStore {

    public settings: AgencyDocumentSettings = new AgencyDocumentSettings();
    public isLoading: boolean = false;
    public isSaving: boolean = false;
    public commonStore: CommonStore;
    public agencyId?: string;

    constructor(commonStore: CommonStore, agencyId?: string) {
        this.commonStore = commonStore;
        this.agencyId = agencyId;
        makeObservable(this, {
            settings: observable,
            isLoading: observable,
            isSaving: observable,
            setMode: action,
            setField: action,
        });
    }

    // MobX does not turn class instances into deep observables, so every change replaces the
    // whole settings object: mutating a field in place would never re-render the tab.
    public setMode(mode: DocumentIssuerMode) {
        this.settings = this.copyWith({mode});
    }

    public setField(field: "name" | "phone" | "address" | "email" | "footerNote", value: string) {
        this.settings = this.copyWith({[field]: value});
    }

    private copyWith(changes: Partial<AgencyDocumentSettings>): AgencyDocumentSettings {
        const current = this.settings;
        return new AgencyDocumentSettings(
            changes.mode ?? current.mode,
            changes.name ?? current.name,
            changes.phone ?? current.phone,
            changes.address ?? current.address,
            changes.email ?? current.email,
            changes.logo ?? current.logo,
            changes.footerNote ?? current.footerNote,
        );
    }

    @action
    public async load() {
        runInAction(() => this.isLoading = true);
        const response = await AgencyDocumentSettingsService.get(this.agencyId);
        this.commonStore.processErrors(response);
        runInAction(() => {
            if (response.success && response.data) {
                this.settings = response.data;
            }
            this.isLoading = false;
        });
    }

    @action
    public async save() {
        // Custom data with no name would print an empty issuer block, and that is exactly what
        // the "no issuer data" option is for.
        if (this.settings.mode === DocumentIssuerMode.CUSTOM && !this.settings.name?.trim()) {
            await this.commonStore.showErrorToast("Escribe al menos el nombre del emisor o usa la opción sin datos del emisor");
            return;
        }

        runInAction(() => this.isSaving = true);
        const response = await AgencyDocumentSettingsService.update(this.settings, this.agencyId);
        this.commonStore.processErrors(response);
        runInAction(() => {
            if (response.success && response.data) {
                this.settings = response.data;
                showSuccessToast("Configuración de documentos guardada.", 4000);
            }
            this.isSaving = false;
        });
    }

    @action
    public async uploadLogo(base64File: string) {
        runInAction(() => this.isSaving = true);
        const response = await AgencyDocumentSettingsService.uploadLogo(base64File, this.agencyId);
        this.commonStore.processErrors(response);
        runInAction(() => {
            if (response.success && response.data) {
                this.settings = response.data;
                showSuccessToast("Logo de los documentos actualizado.", 4000);
            }
            this.isSaving = false;
        });
    }
}

export default DocumentSettingsTabStore;
